import { Platform, type ViewStyle } from "react-native";

/**
 * Cross-platform interaction state styling. Audit task 2.1a.
 *
 * Returns a style delta for hover (web) + focus (web a11y keyboard ring) +
 * disabled. Compose into a primitive's `Pressable` via the style callback:
 *
 * ```tsx
 * <Pressable style={({ pressed, hovered, focused }) => [
 *   baseStyle,
 *   getInteractionDelta({ hovered, focused, disabled }),
 * ]}>
 * ```
 *
 * Press feedback is intentionally NOT covered here — primitives handle
 * pressed scale/opacity via reanimated (see `MoonPressable`) for a smoother
 * spring. Hover + focus are static deltas that don't need animation; press
 * is the perceptually-important one and deserves the smooth path.
 *
 * Pre-baked options (`hoverOpacity`, `focusedColor`) cover the common cases.
 * Callers wanting a different visual treatment (e.g. elevation shift on
 * hover instead of opacity) can ignore this util and apply their own style
 * deltas inside the same callback.
 */
export type InteractionState = {
  pressed?: boolean;
  hovered?: boolean;
  focused?: boolean;
};

export type InteractionStyleOptions = {
  /** Apply the disabled visual (opacity 0.4). */
  disabled?: boolean;
  /** Opacity when hovered (web only). Defaults to 0.85. */
  hoverOpacity?: number;
};

export function getInteractionDelta(
  state: InteractionState,
  options: InteractionStyleOptions = {},
): ViewStyle {
  const { hovered, focused } = state;
  const { disabled, hoverOpacity = 0.85 } = options;

  if (disabled) {
    return { opacity: 0.4 };
  }

  const style: ViewStyle = {};

  if (hovered && Platform.OS === "web") {
    style.opacity = hoverOpacity;
  }

  if (focused && Platform.OS === "web") {
    style.outlineWidth = 0;
  }

  return style;
}
